import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, of, switchMap } from 'rxjs';
import { CookieService } from './cookie.service';
import { AuthService } from './auth.service';

// List of public API endpoints that require a session cookie for SSR
const PUBLIC_API_ENDPOINTS = ['article', 'podcast', 'discover', 'youtube-video'];

export const apiInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  // Run this interceptor only on the server
  // This interceptor only runs on the server.
  if (typeof window !== 'undefined') {
    return next(req);
  }

  const injector = inject(Injector);
  const cookieService = inject(CookieService);
  const authService = inject(AuthService);

  // Check if the request is for a public API that needs a cookie
  const isPublicApiCall = PUBLIC_API_ENDPOINTS.some(endpoint => req.url.includes(endpoint));

  // If it's not a public API call, or if the user is already signed in, proceed normally
  // If it's not a public API call, or if the user is already signed in, proceed normally
  if (!isPublicApiCall) {
    return next(req);
  }

  if (authService.isSignedIn()) {
    return next(req);
  }

  // If it's a public API and the user is not signed in, first call setCookie,
  // then proceed with the original request.
  return cookieService.setCookie().pipe(
    catchError(err => {
      console.error('[API Interceptor - SSR] Error during setCookie call:', JSON.stringify(err));
      return of(null); // Return a null response to allow the chain to continue
    }),
    switchMap((response: HttpResponse<unknown> | null) => {
      const setCookieHeader = response?.headers.get('Set-Cookie');

      if (response && setCookieHeader) {
        // Extract the actual cookie value (e.g., 'authToken=...')
        const cookieValue = setCookieHeader;

        // Clone the original request and add the 'Co
        // okie' header
        const newReq = req.clone({
          headers: req.headers.set('Cookie', cookieValue)
        });
        // console.log('[API Interceptor - SSR] Headers of the new request:', newReq.headers.get('Cookie'));

        return next(newReq);
      } else {
        console.warn('[API Interceptor - SSR] setCookie was called, but no Set-Cookie header was found in the response.');
        return next(req); // Proceed with the original request without the cookie
      }
    })
  );
};
